import { NgTemplateOutlet } from '@angular/common';
import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  contentChildren,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  linkedSignal,
  model,
  NgZone,
  output,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { ConfigStoreService } from '../../services/config.store.service';
import { ContinuousLoadingBarComponent } from '../general/continuous-loading-bar/continuous-loading-bar.component';
import { SearchInputComponent } from '../general/search-input/search-input.component';
import { TruncateTooltipDirective } from '../general/truncate-tooltip.directive';
import { CfgCellDirective } from './table-cell.directive';
import { CfgCellContext, SortState, TableColumn, TableDataSource } from './table-column.model';
import { CfgEmptyDirective } from './table-empty.directive';
import { filterRows, nextSort, sortRows } from './table-sort';

/**
 * Generisk, typesikker tabel for konfiguratorens lister.
 *
 * Datakontrakt: `[resource]` (en ResourceRef/httpResource — se {@link TableDataSource}) driver
 * alle tilstande: loading → loading-bar, fejl → inline fejl + "Prøv igen" (resource.reload()),
 * tom → standardbesked. `[rows]` kan overstyre de viste rækker (joins/overlays) — aldrig tilstanden.
 *
 * Søgning og sortering ejes af tabellen; søgeteksten kan spejles i URLen via `[searchParam]`
 * (deep-linkbare filtre). Kolonner er en config-array; celler kan overstyres med
 * `ng-template[cfgCell]`. Rækkenavigation via `[rowLink]` renderes som ægte ankre (stretched-link pr.
 * celle), så ctrl-/højreklik → ny fane virker. Almindelige celler afkortes ved `[columnMaxWidth]` med
 * tooltip på hover, så én lang værdi ikke skubber resten af rækken ud af syne. Store lister
 * vinduerenderes via en IntersectionObserver-sentinel.
 */
@Component({
  selector: 'cfg-table',
  templateUrl: './table.component.html',
  styleUrl: './table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgTemplateOutlet,
    RouterLink,
    NgbTooltip,
    TruncateTooltipDirective,
    SearchInputComponent,
    ContinuousLoadingBarComponent,
  ],
})
export class CfgTableComponent<T> {
  static readonly SearchStoragePrefix = '[main-configurator-web] table-search:';

  // --- data contract ---
  readonly resource = input.required<TableDataSource<T>>();
  /** Consumer-computed override of `resource.value()` (joins/overlays). Never drives state. */
  readonly rows = input<readonly T[] | undefined>(undefined);
  readonly trackBy = input.required<(row: T) => string | number>();

  // --- columns ---
  readonly columns = input.required<readonly TableColumn<T>[]>();

  // --- search ---
  /** When set, renders the shared search input in the toolbar and filters internally. */
  readonly searchFields = input<readonly (keyof T & string)[] | undefined>(undefined);
  readonly searchPlaceholder = input<string>('Søg...');
  readonly searchAutofocus = input<boolean>(true);
  readonly searchText = model<string>('');
  /** Enter pressed in the search box; emits the currently filtered (and sorted) rows. */
  readonly searchEnter = output<readonly T[]>();
  /**
   * Opt-in URL-synk: navnet på den query-parameter søgeteksten spejles i (fx `search` →
   * `?search=1234`). Sider kan så deep-linke direkte til en filtreret tabel. Begge retninger
   * er lighedsguardede, så param → søgetekst → param aldrig looper, og der skrives med
   * `replaceUrl`, så tastetryk ikke fylder browserhistorikken.
   */
  readonly searchParam = input<string | undefined>(undefined);
  /**
   * Opt-in hukommelse: nøglen søgeteksten gemmes under i sessionStorage (pr. fane), så en
   * søgning overlever at man forlader listen og vender tilbage via menu, dashboard eller
   * redirect efter gem/slet — navigationer der ikke bærer `?search=` med. Lander man uden
   * en udfyldt `[searchParam]`, gendannes den gemte tekst (og spejles så i URLen); en
   * eksplicit parameter vinder altid. Ryddes søgefeltet, ryddes den gemte tekst også.
   */
  readonly searchStorageKey = input<string | undefined>(undefined);

  // --- sorting ---
  /** Shown in the header whenever no explicit sort is active — sorting is never invisible. */
  readonly defaultSort = input<SortState | null>(null);

  // --- navigation ---
  /** Router commands per row; `null` renders no anchor (conditional disable). */
  readonly rowLink = input<((row: T) => any[] | null) | null>(null);
  readonly rowLabel = input<((row: T) => string) | null>(null);
  readonly rowClick = output<T>();

  // --- presentation ---
  readonly height = input<string>('75vh');
  /**
   * Cap for every plain cell's text before it ellipsises (per column: `TableColumn.maxWidth`).
   * Without one, a single long value — a 200-character skabelonnavn — widens its column until the
   * rest of the row is pushed off-screen, and `table-layout: auto` has no opinion about that.
   * The full value stays reachable: truncated cells get a hover tooltip.
   */
  readonly columnMaxWidth = input<string>('24rem');
  readonly emptyText = input<string>('Ingen data at vise.');
  readonly loadingText = input<string>('Indlæser...');
  readonly errorText = input<string>('Fejl under indlæsning af data.');

  // --- windowing ---
  readonly initialWindow = input<number>(50);
  readonly windowChunk = input<number>(50);

  protected readonly cellTemplates = contentChildren(CfgCellDirective);
  protected readonly emptyTemplate = contentChild(CfgEmptyDirective);
  private readonly sentinel = viewChild<ElementRef<HTMLElement>>('sentinel');
  private readonly scrollWrap = viewChild<ElementRef<HTMLElement>>('wrap');

  // --- derivation pipeline: source → filter → sort → window ---
  private readonly sourceRows = computed<readonly T[]>(() => this.rows() ?? this.resource().value() ?? []);
  private readonly hasData = computed(() => this.resource().value() !== undefined);

  protected readonly filteredRows = computed(() =>
    filterRows(this.sourceRows(), this.searchText(), this.searchFields()),
  );

  /** `undefined` = no explicit sort → the table's default sort applies. */
  private readonly sortOverride = signal<SortState | undefined>(undefined);
  protected readonly effectiveSort = computed<SortState | null>(
    () => this.sortOverride() ?? this.defaultSort(),
  );
  protected readonly sortedRows = computed(() =>
    sortRows(this.filteredRows(), this.effectiveSort(), this.columns()),
  );

  /** Resets to the initial window whenever filtering or sorting produces a new array. */
  private readonly windowSize = linkedSignal({
    source: this.sortedRows,
    computation: () => this.initialWindow(),
  });
  protected readonly visibleRows = computed(() => this.sortedRows().slice(0, this.windowSize()));
  protected readonly hasMore = computed(() => this.windowSize() < this.sortedRows().length);

  protected readonly state = computed<'loading' | 'error' | 'empty' | 'data'>(() => {
    if (this.resource().error() !== undefined && !this.hasData()) return 'error';
    if (this.resource().isLoading() && !this.hasData()) return 'loading';
    return this.sortedRows().length === 0 ? 'empty' : 'data';
  });

  private readonly templateMap = computed(() => {
    const map = new Map<string, TemplateRef<CfgCellContext<T>>>();
    for (const directive of this.cellTemplates()) {
      map.set(directive.cfgCell(), directive.templateRef as TemplateRef<CfgCellContext<T>>);
    }
    return map;
  });

  /**
   * The one cell per row whose anchor is announced. Every plain cell renders one so the whole row stays
   * clickable, but a screen reader should hear a single link per row — the others are aria-hidden.
   * Templated cells render no anchor, so this is the first column that isn't templated.
   */
  private readonly linkColumnKey = computed(() => {
    const templates = this.templateMap();
    return this.columns().find(column => !templates.has(column.key))?.key;
  });

  private readonly zone = inject(NgZone);
  private observer: IntersectionObserver | undefined;
  private observerRoot: HTMLElement | undefined;
  private observedElement: HTMLElement | undefined;

  constructor() {
    // --- URL-synk af søgeteksten (kun når [searchParam] er sat) ---
    const route = inject(ActivatedRoute);
    const router = inject(Router);

    // URL → søgetekst: seeder fra parametren ved init og følger eksterne navigationer.
    // Skriver kun ved faktisk afvigelse, så vores egen navigate herunder ikke ekkoer tilbage.
    route.queryParamMap.pipe(takeUntilDestroyed()).subscribe(params => {
      const key = this.searchParam();
      if (!key) return;
      const value = params.get(key) ?? '';
      if (value !== this.searchText()) {
        this.searchText.set(value);
      }
    });

    // Søgetekst → URL: navigerer kun når parametren faktisk afviger (loop-guard den anden vej).
    // Ved landing vinder en udfyldt parameter: abonnementets synkrone første emit sker i
    // konstruktøren, FØR searchParam-inputtet er sat, så seeding dér springes over. Kørte
    // effekten så bare løs, ville den stadig tomme søgetekst rydde parametren igen (deep-links
    // fra fx kunde administration ville blinke og forsvinde). Derfor seeder første kørsel med
    // en nøgle FRA parametren i stedet for at skrive til den.
    let urlSeedApplied = false;
    effect(() => {
      const key = this.searchParam();
      if (!key) return;
      const text = this.searchText();
      const param = route.snapshot.queryParamMap.get(key) ?? '';
      if (!urlSeedApplied) {
        urlSeedApplied = true;
        if (param && param !== text) {
          this.searchText.set(param);
          return;
        }
      }
      if (param === text) return;
      void router.navigate([], {
        relativeTo: route,
        queryParams: { [key]: text || null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    });

    // --- Husket søgetekst (kun når [searchStorageKey] er sat) ---
    // Første kørsel gendanner, hvis hverken søgefeltet eller URL-parametren allerede har en
    // værdi (URL-seedingen ovenfor ejer deep-links — afhængigt af effekt-rækkefølgen har den
    // måske allerede sat søgeteksten, som så blot persisteres). Derefter persisteres hver
    // ændring; den gendannede tekst når URLen via effekten ovenfor (søgetekst ≠ parameter).
    const configStore = inject(ConfigStoreService);
    let storageSeedApplied = false;
    effect(() => {
      const key = this.searchStorageKey();
      if (!key) return;
      const storageKey = CfgTableComponent.SearchStoragePrefix + key;
      const text = this.searchText();
      if (!storageSeedApplied) {
        storageSeedApplied = true;
        const paramKey = this.searchParam();
        const hasParam = !!paramKey && !!route.snapshot.queryParamMap.get(paramKey);
        const stored = configStore.getSession<string>(storageKey);
        if (!text && !hasParam && stored) {
          this.searchText.set(stored);
          return;
        }
      }
      if (text) {
        configStore.setSession(storageKey, text);
      } else {
        configStore.removeSession(storageKey);
      }
    });

    afterRenderEffect(() => {
      // Re-run after every data render so an already-visible sentinel re-fires (unobserve/observe below).
      this.visibleRows();
      const root = this.scrollWrap()?.nativeElement;
      const sentinelElement = this.sentinel()?.nativeElement;
      if (!root || !sentinelElement) {
        this.observer?.disconnect();
        this.observer = this.observerRoot = this.observedElement = undefined;
        return;
      }
      if (!this.observer || this.observerRoot !== root) {
        this.observer?.disconnect();
        this.observer = new IntersectionObserver(
          entries => {
            if (entries.some(entry => entry.isIntersecting)) {
              this.zone.run(() => this.growWindow());
            }
          },
          { root, rootMargin: '150px' },
        );
        this.observerRoot = root;
        this.observedElement = undefined;
      }
      if (this.observedElement) {
        this.observer.unobserve(this.observedElement);
      }
      this.observer.observe(sentinelElement);
      this.observedElement = sentinelElement;
    });

    inject(DestroyRef).onDestroy(() => this.observer?.disconnect());
  }

  protected growWindow(): void {
    this.windowSize.update(size => Math.min(size + this.windowChunk(), this.sortedRows().length));
  }

  protected isSortable(column: TableColumn<T>): boolean {
    return column.sortable ?? true;
  }

  protected cycleSort(column: TableColumn<T>): void {
    this.sortOverride.set(nextSort(column.key, this.effectiveSort(), this.defaultSort()));
  }

  protected ariaSortFor(column: TableColumn<T>): 'ascending' | 'descending' | null {
    const sort = this.effectiveSort();
    if (!sort || sort.key !== column.key) return null;
    return sort.direction === 'asc' ? 'ascending' : 'descending';
  }

  protected sortMarker(column: TableColumn<T>): string | null {
    const sort = this.effectiveSort();
    if (!sort || sort.key !== column.key) return null;
    return sort.direction === 'asc' ? 'fa-chevron-up' : 'fa-chevron-down';
  }

  protected templateFor(key: string): TemplateRef<CfgCellContext<T>> | undefined {
    return this.templateMap().get(key);
  }

  protected resolveLink(row: T): any[] | null {
    const linkFn = this.rowLink();
    return linkFn ? linkFn(row) : null;
  }

  protected isLinkColumn(column: TableColumn<T>): boolean {
    return column.key === this.linkColumnKey();
  }

  protected maxWidthFor(column: TableColumn<T>): string {
    return column.maxWidth ?? this.columnMaxWidth();
  }

  protected cellValue(row: T, column: TableColumn<T>): unknown {
    return column.value ? column.value(row) : (row as Record<string, unknown>)[column.key];
  }

  protected cellContext(row: T, column: TableColumn<T>, index: number): CfgCellContext<T> {
    return { $implicit: row, row, value: this.cellValue(row, column), index, column };
  }

  protected rowLabelFor(row: T, column: TableColumn<T>): string {
    const labelFn = this.rowLabel();
    return labelFn ? labelFn(row) : String(this.cellValue(row, column) ?? '');
  }

  protected onSearchEnter(): void {
    this.searchEnter.emit(this.sortedRows());
  }
}
